import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Copy,
  Form,
  Input,
  SettingsBuilder,
  SubmitButton,
  Loading,
} from '../../../../components';
import {
  defaultForm,
  useWebLinkGenerator,
} from '../../hooks/useWebLinkGenerator';
import widgetSchema from '../json/widgetSchema.json';
import widgetUiSchema from '../json/widgetUiSchema.json';
import { notify } from '../../../../app';

import { generateCompressedLink } from '../utils/generateLink';
import { getWebLinkConfig } from '../utils/getWebLinkConfig';
import { cleanFormDataByTypeLink } from '../utils/cleanFormDataByTypeLink';
import {
  pageBaseUrl,
  widgetDemoUrl,
  widgetIframeUrl,
  widgetImageUrl,
} from '../utils/const';
import type { WebLinkGeneratorFormProps } from '../../hooks/useWebLinkGenerator';

type WebLinGeneratorProps = {
  agentId: string;
  compressed?: string;
  originalUrl?: string;
  onCompress?: (data: any) => void;
};

export const LongLinkGenerator: React.FC<WebLinGeneratorProps> = ({
  agentId,
  originalUrl,
  compressed,
  onCompress,
}) => {
  const { onChange, form, formId, setFormId, setForm } = useWebLinkGenerator({
    agentId,
  });

  const [lastTypeLink, setLastTypeLink] = useState<string | null>(null);

  const generatedLink = useMemo(
    () =>
      (compressed &&
        `${form?.web_link_generator?.baseUrl}/#config=${compressed}`) ||
      '',
    [compressed, form?.web_link_generator?.baseUrl]
  );

  const handleCompress = useCallback(() => {
    const compress = async () => {
      try {
        const webLinkConfig = getWebLinkConfig({
          ...form.web_link_generator,
        });

        const result = await generateCompressedLink(webLinkConfig);
        onCompress?.({
          compressed: result,
          generatedLink: `${form?.web_link_generator?.baseUrl}/#config=${result}`,
        });
        notify.success('Link created successfully');
      } catch (err) {
        notify.error('Compression failed');
      }
    };
    compress();
  }, [form.web_link_generator, onCompress]);

  const setNestedFormField = useCallback(
    (field: string, value: any) => {
      setForm(prev => ({
        ...prev,
        web_link_generator: {
          ...prev.web_link_generator,
          [field]: value,
        },
      }));
      setFormId(f => f + 1);
    },
    [setForm, setFormId]
  );

  const handleFormChange = useCallback(
    (newForm: WebLinkGeneratorFormProps) => {
      const currentType = newForm?.web_link_generator?.typeLink;

      if (currentType && currentType !== lastTypeLink) {
        setLastTypeLink(currentType);

        if (currentType === 'page') {
          const cleaned = cleanFormDataByTypeLink(newForm.web_link_generator);

          setForm({
            ...cleaned,
            web_link_generator: {
              agentId,
              ...cleaned,
              variantLink: undefined,
              baseUrl: pageBaseUrl,
            },
          });
        }

        if (currentType === 'widget') {
          const baseUrl = defaultForm?.baseUrl;
          let url = `${baseUrl}/demo`;

          const updatedForm = {
            ...newForm,
            web_link_generator: {
              ...defaultForm,
              ...newForm.web_link_generator,
              variantLink: 'default',
              agentId,
              baseUrl: url,
            },
          };
          setForm(updatedForm as WebLinkGeneratorFormProps);
        }

        setFormId(f => f + 1);
      }
    },
    [agentId, lastTypeLink, setForm, setFormId]
  );

  const handleIframeUrlChange = useCallback(() => {
    const { iframeUrl, variantLink } = form?.web_link_generator;

    const expectedIframeUrl = variantLink !== 'iframe' ? undefined : iframeUrl;

    if (iframeUrl !== expectedIframeUrl) {
      setNestedFormField('iframeUrl', expectedIframeUrl);
    }
  }, [form?.web_link_generator, setNestedFormField]);

  const handleBaseUrlChange = useCallback(() => {
    const { baseUrl, variantLink, typeLink } = form?.web_link_generator;
    let newBaseUrl = pageBaseUrl;

    if (typeLink === 'widget') {
      if (variantLink === 'default') {
        newBaseUrl = widgetDemoUrl;
      } else if (variantLink === 'image') {
        newBaseUrl = widgetImageUrl;
      } else if (variantLink === 'iframe') {
        newBaseUrl = widgetIframeUrl;
      }
    }

    if (baseUrl !== newBaseUrl) {
      setNestedFormField('baseUrl', newBaseUrl);
    }
  }, [form?.web_link_generator, setNestedFormField]);

  const handleMobileWebsiteImageUrlChange = useCallback(() => {
    const variantLink = form.web_link_generator?.variantLink;
    const mobileWebsiteImageUrl =
      form.web_link_generator?.mobileWebsiteImageUrl;
    const desktopWebsiteImageUrl =
      form.web_link_generator?.desktopWebsiteImageUrl;

    const expectedMobileWebsiteImageUrl =
      variantLink !== 'image' ? undefined : mobileWebsiteImageUrl;
    const expectedDesktopWebsiteImageUrl =
      variantLink !== 'image' ? undefined : desktopWebsiteImageUrl;

    if (
      form.web_link_generator?.mobileWebsiteImageUrl !==
        expectedMobileWebsiteImageUrl ||
      form.web_link_generator?.desktopWebsiteImageUrl !==
        expectedDesktopWebsiteImageUrl
    ) {
      setNestedFormField(
        'mobileWebsiteImageUrl',
        expectedMobileWebsiteImageUrl
      );
      setNestedFormField(
        'desktopWebsiteImageUrl',
        expectedMobileWebsiteImageUrl
      );
    }
  }, [
    form.web_link_generator?.desktopWebsiteImageUrl,
    form.web_link_generator?.mobileWebsiteImageUrl,
    form.web_link_generator?.variantLink,
    setNestedFormField,
  ]);

  const handleOpenLink = useCallback(() => {
    if (compressed) {
      window.open(
        generatedLink || originalUrl,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [compressed, generatedLink, originalUrl]);

  useEffect(() => {
    handleFormChange(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleIframeUrlChange();
    handleMobileWebsiteImageUrlChange();
    handleBaseUrlChange();
  }, [
    form,
    handleBaseUrlChange,
    handleIframeUrlChange,
    handleMobileWebsiteImageUrlChange,
  ]);

  return (
    <>
      <div className="flex flex-col">
        {!form.web_link_generator?.agentId ? (
          <Loading />
        ) : (
          <Form
            key={String(formId || 'webLinkGenerator')}
            onChange={onChange}
            initialValue={form}
          >
            <div className="flex flex-row gap-x-md justify-end">
              {(compressed || originalUrl) && (
                <SubmitButton
                  label="Open long link"
                  size="medium"
                  type="empty"
                  onClick={handleOpenLink}
                  className="text-main border border-solid border-border px-2 py-1 rounded-md bg-white hover:opacity-80 transition-opacity duration-150"
                />
              )}
              <SubmitButton
                label="Generate long link"
                size="medium"
                type="primary"
                onClick={() => handleCompress()}
              />
            </div>
            <SettingsBuilder
              showCustomError={false}
              name="web_link_generator"
              schemaData={{
                jsonSchema: widgetSchema,
                uiSchema: widgetUiSchema,
              }}
            />
            <div className="flex flex-col gap-x-md my-4">
              <div className="flex flex-row justify-end gap-x-md">
                <div className="text-small">Copy JSON</div>
                <Copy
                  className="shrink-0"
                  text={JSON.stringify(form.web_link_generator) ?? ''}
                />
              </div>
              <Input
                value={JSON.stringify(form.web_link_generator, null, 2) || ''}
                multiline
                readonly
              />
            </div>
          </Form>
        )}
      </div>
    </>
  );
};
